'use client';

import { useState, useEffect, createElement } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { colors, typography, radius, size } from '../../styles/design-tokens';
import { NavItems, NavItem as NavItemType } from './NavItems';
import { BASE_PERMISSIONS, isAdminRole } from '../../lib/role';
import { NavbarSkeleton } from '../skeletons';
import { NavbarPermissionGuard } from './NavbarPermissionGuard';
import { baseNavItems, NavItemConfig } from '../../lib/navConfig';

interface NavItem extends NavItemType {
  requiredPermissions: string[];
  baseRoute: string; // Route without prefix (e.g., 'dashboard', 'student-management')
  adminOnly?: boolean; // New flag for admin-only items
}

// Simple helper function to build routes based on user role
const buildRouteForRole = (baseRoute: string, userRole: string): string => {
  const isAdmin = isAdminRole(userRole);
  return isAdmin ? `/admin/${baseRoute}` : `/${baseRoute}`;
};

const mobileBreakpoints = {
  sm: {
    breakpoint: 500,
    moreItems: ['Transportation'],
  },
  xs: {
    breakpoint: 420,
    moreItems: ['Transportation', 'Grades'],
  },
};

const Navbar = () => {
  const pathname = usePathname() || '';
  const [mounted, setMounted] = useState(false);
  const { data: session, status: sessionStatus } = useSession();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Show skeleton while mounting or while session/permissions are loading.
  // This prevents a transient state where only items with no required
  // permissions (e.g., "Assessments") appear before permissions are fetched.
  if (!mounted || sessionStatus === 'loading') return <NavbarSkeleton />;

  // Get user role and permissions
  const userRole = session?.user?.role || '';
  const userPermissions = session?.user?.permissions || [];

  // Convert shared config to navbar format and generate nav items with dynamic URLs
  const navItems = baseNavItems.map((item: NavItemConfig) => ({
    label: item.label,
    baseRoute: item.baseRoute,
    href: buildRouteForRole(item.baseRoute, userRole),
    icon: createElement(item.icon, { size: 20 }),
    requiredPermissions: item.requiredPermissions,
    adminOnly: item.adminOnly,
  }));

  // Filter navItems based on permissions and role
  const filteredNavItems = navItems.filter((item) => {
    // Hide Reports tab
    if (item.label === 'Reports') {
      return false;
    }

    // Hide admin-only items from non-admin users
    if (item.adminOnly && !isAdminRole(userRole)) {
      return false;
    }

    // Check if user has any of the required permissions
    const hasPermission =
      item.requiredPermissions.length === 0 ||
      item.requiredPermissions.some((permission) =>
        userPermissions.includes(permission),
      );

    return hasPermission;
  });

  // Helper function to check if route is active
  const isActiveRoute = (pathname: string, baseRoute: string): boolean => {
    // Keep Fees highlighted for any nested route under /fees
    if (baseRoute.startsWith('fees')) {
      const feesBase = buildRouteForRole('fees', userRole);
      return pathname.startsWith(feesBase);
    }
    const fullRoute = buildRouteForRole(baseRoute, userRole);
    return pathname.includes(fullRoute);
  };

  // Dynamic sub-routes based on user role
  const routePrefix = isAdminRole(userRole) ? '/admin' : '';

  const teamTabs = [
    { label: 'Teachers', href: `${routePrefix}/team/teachers` },
    { label: 'Transportation', href: `${routePrefix}/team/transportation` },
    { label: 'Other Staff', href: `${routePrefix}/team/supporting-staff` },
  ];

  const feeTabs = [
    { label: 'Receipts', href: `${routePrefix}/fees/receipts` },
    { label: 'Fee Configuration', href: `${routePrefix}/fees/configuration` },
    { label: 'Pending Fees', href: `${routePrefix}/fees/pending` },
  ];

  const isTeamPage = pathname.includes('/team');
  const isFeePage = pathname.includes('/fees');

  return (
    <>
      {/* Desktop Sidebar Navigation */}
      <div
        className="hidden [@media(min-width:900px)]:block fixed left-0 top-[var(--top-navbar-height)] bottom-0 w-64 bg-white border-r border-[#E5E7EB] z-40 [@media(min-width:1921px)]:left-[calc((100%-1920px)/2)]"
        style={{ height: 'calc(100vh - var(--top-navbar-height))' }}
      >
        <div className="h-full overflow-y-auto pt-2">
          <div className="px-4 py-2 space-y-1">
            {filteredNavItems.map((item) => {
              const isActive = isActiveRoute(pathname, item.baseRoute);
              return (
                <NavbarPermissionGuard key={item.label} item={item}>
                  <Link
                    href={item.href}
                    className={`flex items-center w-full px-4 py-3 text-sm rounded-xl transition-colors ${
                      isActive
                        ? 'text-[#0D373D] bg-[#E2FDCB]'
                        : 'text-[#4D4F52] hover:bg-[#F5F8F9]'
                    }`}
                  >
                    {item.icon}
                    <span className="ml-3 flex items-center gap-1.5">
                      {item.label}
                      {item.label === 'Assessments' && (
                        <span
                          className={`px-1.5 py-0.5 text-[11px] font-medium rounded leading-none ${
                            isActive
                              ? 'bg-[#0D373D]/10 text-[#0D373D]'
                              : 'bg-[#F3F4F6] text-[#6B7280]'
                          }`}
                        >
                          BETA
                        </span>
                      )}
                    </span>
                  </Link>
                </NavbarPermissionGuard>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content Wrapper */}
      <div className="[@media(min-width:900px)]:ml-64">
        {/* Content goes here */}
      </div>

      {/* Mobile Bottom Navigation */}
      <div
        className={`[@media(min-width:900px)]:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[${colors.gray[100]}]`}
      >
        <NavItems
          pathname={pathname}
          isMobile={true}
          mobileBreakpoints={mobileBreakpoints}
          navItems={filteredNavItems}
        />
      </div>
    </>
  );
};

export default Navbar;
